/**
 * User management module (add, edit, delete users)
 */

import { showNotification } from './notifications.js';

// Password visibility toggle
export function togglePassword(fieldId) {
    const field = document.getElementById(fieldId);
    if (!field) return;

    const toggle = field.parentNode.querySelector('.password-toggle');
    if (!toggle) return;

    const showIcon = toggle.querySelector('.password-show');
    const hideIcon = toggle.querySelector('.password-hide');

    if (field.type === 'password') {
        field.type = 'text';
        if (showIcon) showIcon.style.display = 'none';
        if (hideIcon) hideIcon.style.display = 'block';
    } else {
        field.type = 'password';
        if (showIcon) showIcon.style.display = 'block';
        if (hideIcon) hideIcon.style.display = 'none';
    }
}

// Password strength checker
export function checkPasswordStrength(password) {
    let score = 0;

    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    if (/(.)\1{2,}/.test(password)) score -= 1;
    if (/123|abc|qwe|password/i.test(password)) score -= 2;

    if (score <= 2) {
        return { score: 1, feedback: 'Weak', class: 'weak' };
    } else if (score <= 4) {
        return { score: 2, feedback: 'Fair', class: 'fair' };
    } else if (score <= 5) {
        return { score: 3, feedback: 'Good', class: 'good' };
    } else {
        return { score: 4, feedback: 'Strong', class: 'strong' };
    }
}

// Update password strength indicator
export function updatePasswordStrength(password) {
    const strengthFill = document.getElementById('strengthFill');
    const strengthText = document.getElementById('strengthText');

    if (!strengthFill || !strengthText) return;

    const strength = checkPasswordStrength(password);
    const percentage = (strength.score / 4) * 100;

    strengthFill.style.width = percentage + '%';
    strengthFill.className = `strength-fill ${strength.class}`;
    strengthText.textContent = strength.feedback;
    strengthText.className = `strength-text ${strength.class}`;
}

// Generate random password
export function generateRandomPassword() {
    const length = 16;
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    const allChars = lowercase + uppercase + numbers + symbols;

    let password = '';
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    for (let i = 4; i < length; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    password = password.split('').sort(() => 0.5 - Math.random()).join('');

    const passwordField = document.getElementById('password');
    const confirmField = document.getElementById('confirm_password');

    if (passwordField) {
        passwordField.value = password;
        passwordField.type = 'text';
        updatePasswordStrength(password);

        setTimeout(() => {
            passwordField.type = 'password';
        }, 3000);
    }

    if (confirmField) {
        confirmField.value = password;
    }

    showNotification('Password generated successfully!', 'success');
}

// Fill sample user data
export function fillSampleUser() {
    const sampleData = {
        username: 'john.doe',
        email: 'john.doe@example.com',
        role: 'user'
    };

    Object.keys(sampleData).forEach(key => {
        const field = document.getElementById(key);
        if (field) {
            field.value = sampleData[key];
        }
    });

    showNotification('Sample data filled!', 'info');
}

// User form validation
export function validateUserAddForm() {
    let isValid = true;
    const errors = [];

    const username = document.getElementById('username')?.value.trim();
    const email = document.getElementById('email')?.value.trim();
    const password = document.getElementById('password')?.value;
    const confirmPassword = document.getElementById('confirm_password')?.value;

    if (!username) {
        errors.push('Username is required');
        isValid = false;
    } else if (username.length < 3) {
        errors.push('Username must be at least 3 characters long');
        isValid = false;
    } else if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        errors.push('Username can only contain letters, numbers, and underscores');
        isValid = false;
    }

    if (!email) {
        errors.push('Email is required');
        isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push('Please enter a valid email address');
        isValid = false;
    }

    if (!password) {
        errors.push('Password is required');
        isValid = false;
    } else if (password.length < 8) {
        errors.push('Password must be at least 8 characters long');
        isValid = false;
    }

    if (password !== confirmPassword) {
        errors.push('Passwords do not match');
        isValid = false;
    }

    if (!isValid) {
        showNotification(errors.join('\n'), 'error');
    }

    return isValid;
}

// User edit form validation
export function validateUserEditForm() {
    let isValid = true;
    const errors = [];

    const username = document.getElementById('username')?.value.trim();
    const email = document.getElementById('email')?.value.trim();
    const password = document.getElementById('password')?.value;
    const confirmPassword = document.getElementById('confirm_password')?.value;

    if (!username) {
        errors.push('Username is required');
        isValid = false;
    } else if (username.length < 3) {
        errors.push('Username must be at least 3 characters long');
        isValid = false;
    }

    if (!email) {
        errors.push('Email is required');
        isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push('Please enter a valid email address');
        isValid = false;
    }

    if (password) {
        if (password.length < 8) {
            errors.push('Password must be at least 8 characters long');
            isValid = false;
        }

        if (password !== confirmPassword) {
            errors.push('Passwords do not match');
            isValid = false;
        }
    }

    if (!isValid) {
        showNotification(errors.join('\n'), 'error');
    }

    return isValid;
}

// Delete user confirmation modal
export function confirmDeleteUser(username, userId) {
    const modal = document.getElementById('deleteUserModal');
    const userNameSpan = document.getElementById('deleteUserName');
    const userIdField = document.getElementById('deleteUserId');

    if (modal && userNameSpan && userIdField) {
        userNameSpan.textContent = username;
        userIdField.value = userId;
        modal.style.display = 'flex';

        const cancelBtn = modal.querySelector('.btn-outline');
        if (cancelBtn) {
            setTimeout(() => cancelBtn.focus(), 100);
        }
    }
}

// Close delete modal
export function closeDeleteModal() {
    const modal = document.getElementById('deleteUserModal');
    if (modal) {
        modal.style.display = 'none';
    }
}

// Setup password strength monitoring
export function setupPasswordStrength() {
    const passwordField = document.getElementById('password');
    if (passwordField) {
        passwordField.addEventListener('input', function() {
            updatePasswordStrength(this.value);
        });
    }

    const modal = document.getElementById('deleteUserModal');
    if (modal) {
        modal.addEventListener('click', function(e) {
            if (e.target === modal) {
                closeDeleteModal();
            }
        });
    }

    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
            closeDeleteModal();
        }
    });
}

// Make functions available globally
window.togglePassword = togglePassword;
window.generateRandomPassword = generateRandomPassword;
window.fillSampleUser = fillSampleUser;
window.validateUserAddForm = validateUserAddForm;
window.validateUserEditForm = validateUserEditForm;
window.confirmDeleteUser = confirmDeleteUser;
window.closeDeleteModal = closeDeleteModal;
